import { Injectable, inject } from "@angular/core";
import { BaseServiceExt } from "@providers/BaseServiceExt";
import { DeviceService } from "@services/device.service";
import { C_CategoryInfo, C_EventLive, C_GameGroupByName, C_Result, C_SportsEventLive } from "@sports-models/index";
import { FastBetProvider } from "@sports-services/fast-bet.service";
import { SportsConstants } from "@sports-utils/index";
import { SportHandleList } from "@sports-utils/sport-handle-list";
import { ListOfGameTypesInCombinedMarket, ResultadosFinalesGameTypes } from "@sports-utils/sports.constants";
import { Observable, map } from "rxjs";
import { SportsCommonService } from "./sports.common.service";

@Injectable({ providedIn: "root" })
export class SportLiveMarketService extends BaseServiceExt {
  deviceService = inject(DeviceService);
  sportsCommonService = inject(SportsCommonService);
  fastBetService = inject(FastBetProvider);

  isDesktop: boolean;
  isMobile: boolean;

  //GameType property used for identify "Resultados Finales"
  gametypes = ResultadosFinalesGameTypes;
  listOfGameTypesInCombinedMarket = ListOfGameTypesInCombinedMarket;
  regexHandicap = /[+-.0-9]+/g;
  regexTitles = /[a-zA-Z]+/;
  regexValues = /[0-9-.+-]+/;
  regexValuesLongResult = /[0-9-+.]+\d/g;

  constructor() {
    super();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();
  }

  getGamesLiveEvent2(args): Observable<any> {
    const url = `${this.globalVars.NAVIGATIONSERVICEBASEURL}${SportsConstants.getGamesLiveByCategoryInfoUrl}`;
    const headers = { language: this.globalVars.Idioma };
    const params: any = {};
    params.parentid = args.parentId;
    params.categoryInfoId = args.categoryInfoId;

    const resultIdsParams = args.nodeIdList?.length && args.nodeIdList.join(";");
    if (resultIdsParams) params.resultIds = resultIdsParams;
    return this.myGet(url, true, { headers, params }).pipe(
      map((data) => {
        if (!data || !data.Event || !data.CategoryInfos?.length) {
          throw new Error("El evento no está activo en estos momentos.");
        }
        data.Event = C_SportsEventLive.parse(data.Event);
        data.Event.setHomeAndAway();
        data.Event.setDetailScoreboardType(data.Event);
        data.Event.Games.forEach((game) => {
          this.sportsCommonService.getGameResultsOrdered(game);
          this.sportsCommonService.setLockedResults(data.Event);
        });

        let groupedMarkets = this.sportsCommonService.groupMarkets(data.Event.Games, args.leagueName);

        const alternatives = [
          SportHandleList.baseball,
          SportHandleList.basketball,
          SportHandleList.american_football,
        ];
        if (args.sportHandle && alternatives.indexOf(args.sportHandle) !== -1) {
          groupedMarkets = this.sportsCommonService.doAlternatives(data.Event.Games, groupedMarkets);
        }

        groupedMarkets.forEach((grouped) => {
          grouped.sort();
        });

        data.Event.Games = groupedMarkets.sort((a, b) => b.Priority - a.Priority);
        return data;
      }),
    );
  }

  getBetsenseData(NodeId) {
    const url = `https://ctrl2.betsense.io/api/1/7/1/delivery/events/es/${NodeId}`;
    return this.myGet2(url);
  }

  updateOddChanges(prev: any, current: any, field: string = "Results") {
    if (!prev || !current) {
      return;
    }
    field = current[field] ? field : "GroupResults";
    for (const i in current[field]) {
      if (!prev[field] || !prev[field][i] || !current[field][i]) {
        return;
      }
      current[field][i].upOdd = current[field][i].Odd > prev[field][i].Odd;
      current[field][i].downOdd = current[field][i].Odd < prev[field][i].Odd;
    }
    return current;
  }

  setEvents(event, filter, market) {
    if (!filter || (Boolean(filter) && filter.CategoryId === SportsConstants.NineHundredNinetyNineCategoryId)) {
      return;
    }
    /* combined markets */
    //Fastbet
    if (this.isTennisEvent(event) && filter.CategoryId === SportsConstants.FastbetCategoryId) {
      event.Games = this.getFastBets(event, market);
      return event;
    }

    const filteredMarkets = event.Games.filter((game) => {
      switch (true) {
        case !game.CategoryInfos:
          return false;
        case game.CategoryInfos.length > 0 && filter.CategoryId !== SportsConstants.BetbuilderCategoryId:
          return game.CategoryInfos.find((category) => category.CategoryId === filter.CategoryId);
        case filter.CategoryId === SportsConstants.BetbuilderCategoryId:
          return game.SmartMarketAvailable;
        default:
          return filter.CategoryId === SportsConstants.PrincipalesCategoryId;
      }
    });

    event.Games.forEach((game, index) => {
      game = {
        ...game,
        teamHome: market.teamHome,
        teamAway: market.teamAway,
        nameLeague: market.LeagueName,
      };
      game.GroupResults.forEach((r) => {
        r.Spov = game.Spov;
      });

      this.getAllSpovesInMarket(filteredMarkets, game);
    });
    this.getCombinedMarkets(event.Games, market, true);

    event.Games = filteredMarkets;
    if (event.SportHandle === "tennis") {
      event.Games = event.Games.filter((game) => !game.Locked);
    }
    return event;
  }

  public getCombinedMarkets(markets, actualMarket, skipCall = false) {
    if (this.globalVars.FEATURES.ListOfGameTypesInCombinedMarket) {
      this.listOfGameTypesInCombinedMarket = JSON.parse(this.globalVars.FEATURES.ListOfGameTypesInCombinedMarket);
    }
    const updatedMarkets = markets.map((market) => {
      if (!market.GroupResults) return;
      const idCombinedMarket = this.listOfGameTypesInCombinedMarket.find(
        (idMarket) => idMarket === market.GameType,
      );
      market = {
        ...market,
        teamAway: market.teamAway ? market.teamAway : actualMarket.teamAway,
        teamHome: market.teamHome ? market.teamHome : actualMarket.teamHome,
      };
      if (idCombinedMarket) {
        market = {
          ...market,
          isCombined: true,
          displayMode: "single",
          headerClass: "sb-grid-header--single",
          DisplayTypeName: "3buttonlist",
          Locked: actualMarket.Locked ? actualMarket.Locked : market.Locked,
        };

        this.getCombinedMarketMatrix(idCombinedMarket, market, actualMarket, skipCall);
      } else {
        market.isCombined = false;
        market.displayMode = "expanded";
      }
      return market;
    });
    return updatedMarkets;
  }

  // LIVE MARKET CREATION
  private getCombinedMarketMatrix(idCombinedMarket, market, actualMarket, skipCall: boolean = false) {
    let matrixGrid: number[] = [];
    switch (idCombinedMarket) {
      case 39:
      case 175:
      case 148:
      case 1065:
      case 991:
      case 1667:
      case 1668:
      case 1669:
      case 1805:
      case 1806:
      case 1804:
      case 1394:
        matrixGrid = [0, 2, 4, 1, 3, 5];
        this.getCombinedMarketMatchResultAndTotalOverUnder(market, matrixGrid, skipCall);
        break;
      case 989:
        matrixGrid = [0, 2, 1, 3];
        this.getCombinedMarketMatchResultAndTotalOverUnder(market, matrixGrid, skipCall);
        break;
      case 7:
        matrixGrid = [0, 1, 2, 3, 4, 5, 7, 6, 8];
        this.getCombinedMarketMatchResultAndFirstTeamScore(market, matrixGrid);
        break;
      case 988:
      case 990:
        matrixGrid = [0, 2, 4, 1, 3, 5];
        this.getCombinedMarketMatchResultAndFirstTeamScore(market, matrixGrid);
        break;
      case 1064:
        matrixGrid = [0, 2, 4, 1, 3, 5];
        this.getCombinedMarketMatchResultAndFirstTeamScore(market, matrixGrid);
        break;
      case 1330:
      case 1809:
      case 1807:
      case 1808:
        matrixGrid = [0, 2, 1, 3];
        this.getCombinedMarketLineMatchAndTotal(market, matrixGrid, actualMarket);
        break;
      case 1391:
      case 1392:
      case 1393:
        matrixGrid = [0, 1, 2, 3];
        this.getCombinedMarketLineMatchAndTotal(market, matrixGrid, actualMarket);
        break;
      case 1647:
      case 1648:
      case 1649:
      case 1650:
      case 1606 /* especial basket case */:
        /*handicap*/
        matrixGrid = [0, 2, 1, 3];
        this.getCombinedMarketLineMatchAndTotal(market, matrixGrid, actualMarket);
        break;
      case 993:
        matrixGrid = [0, 2, 1, 3];
        this.getCombinedMarketHandicap(market, matrixGrid, actualMarket);
        break;
      case 4:
      case 155:
        matrixGrid = [0, 1, 2, 3];
        this.getCombinedMarketOneXTwoHandicap(market, matrixGrid, actualMarket);
        break;
      case 1853:
        matrixGrid = [0, 2, 1, 3];
        this.getCombinedMarketHandicapBasket(market, matrixGrid, actualMarket);
        break;
      default:
        return;
    }
  }

  private getCombinedMarketHandicapBasket(market, matrix: number[], actualMarket) {
    market.isHardCombined = false;
    market.isSoftCombined = true;
    market.titleCenter = "";
    market.DisplayTypeName = "2buttonline";
    market.teamAway = actualMarket.teamAway;
    market.teamHome = actualMarket.teamHome;
    this.getTotalsAndHandicaps(market);
    this.getGridWithFourPositions(market, matrix);
  }

  private getCombinedMarketOneXTwoHandicap(market: any, matrix: number[], actualMarket: any) {
    market.isHardCombined = false;
    market.isCombined = false;
    market.isSoftCombined = false;
    market.isHandicap = true;
    market.titleCenter = `X(${this.getShortName(market.teamAway)})`;
    market.DisplayTypeName = "3buttonlist";
    market.displayMode = "expanded";
    market.headerClass = "sb-grid-header";
    market.teamHome = this.getShortName(market.teamHome);
    market.teamAway = this.getShortName(market.teamAway);
    market.GroupResults.forEach((result: any) => {
      if (!result.Name || !result.Name.match(this.regexValuesLongResult)) {
        return;
      }
      const [namesOne, nameTwo] = result.Name.match(this.regexValuesLongResult);
      result.Name = nameTwo ? nameTwo : namesOne;
    });
  }

  private getCombinedMarketHandicap(market: any, matrix: number[], actualMarket: any) {
    market.isHardCombined = false;
    market.isSoftCombined = true;
    market.titleCenter = "";
    market.DisplayTypeName = "2buttonline";
    market.teamAway = actualMarket.teamAway;
    market.teamHome = actualMarket.teamHome;
    this.getTotalsAndHandicaps(market);
    this.getGridWithFourPositions(market, matrix);
  }

  private getCombinedMarketMatchResultAndTotalOverUnder(
    market: any,
    matrix?: number[],
    skipCall: boolean = false,
  ) {
    market.titleCenter = "X";
    market.teamHome = "1";
    market.teamAway = "2";
    market.isHardCombined = true;
    market.isSoftCombined = false;
    //eslint-disable-next-line
    market.Locked = market.Locked;
    if (market.GameType === 1065) {
      market.titleCenter = "1/2";
      market.teamHome = "1/X";
      market.teamAway = "2/X";
    }
    if (market.GameType === 989) {
      market.DisplayTypeName = "2buttonlist";
      market.titleCenter = undefined;
      market.teamHome = "Sí";
      market.teamAway = "No";
    }
    if (!skipCall) {
      this.getTotalSpovsList(market);
      this.getGridWithSixPositions(market, matrix);
    }
  }

  private getCombinedMarketLineMatchAndTotal(market, matrix: number[], actualMarket) {
    market.isHardCombined = false;
    market.isSoftCombined = true;
    market.titleCenter = "";
    market.DisplayTypeName = "2buttonline";
    // eslint-disable-next-line no-self-assign
    market.Locked = market.Locked;

    if (market.GameType === 1330 || market.GameType === 1853) {
      this.getTotalSpovsList(market);
      market.teamHome = `${this.getShortName(actualMarket.teamHome)}`;
      market.teamAway = `${this.getShortName(actualMarket.teamAway)}`;
    } else {
      this.getTotalsAndHandicaps(market);
    }
    this.getGridWithFourPositions(market, matrix);
  }

  private getCombinedMarketMatchResultAndFirstTeamScore(market: any, matrix: number[]) {
    market.titleCenter = "1/2";
    market.teamHome = "1/X";
    market.teamAway = "2/X";
    //eslint-disable-next-line
    market.Locked = market.Locked;
    if (market.GameType === 7 || market.GameType === 988 || market.GameType === 990) {
      market.titleCenter = "X";
      market.teamHome = "1";
      market.teamAway = "2";
      market.DisplayTypeName = "3buttonlist";
    }
    market.isHardCombined = false;
    market.isSoftCombined = true;
    market.AllSpov = this.getAllSpovsWithOutSpovs(market);
    this.getGridWithSevenPositions(market, matrix);
  }

  private getShortName(name: string) {
    return name.replace(/\s/g, "").slice(0, 3).toUpperCase();
  }

  private getTotalsAndHandicaps(market) {
    const keysObject = {};
    const newArraySpovs = [];
    market.spovListOfKeys = {};

    const otherSpoves = market.Spov.split("|");
    otherSpoves.forEach((key) => {
      if (key === "") {
        return;
      }
      keysObject[key.match(this.regexTitles)[0]] = key.match(this.regexValues)[0];
    });
    market.spovListOfKeys = keysObject;
    market.spovListOfKeys.realHcap = market.spovListOfKeys.Hcap;
    if (Math.sign(parseFloat(market.spovListOfKeys.Hcap)) === -1) {
      market.spovListOfKeys.Hcap = parseFloat(market.spovListOfKeys.Hcap) * -1;
    }

    if (
      market.GameType === 993 ||
      market.GameType === 1606 ||
      market.GameType === 1648 ||
      market.GameType === 1649 ||
      market.GameType === 1650 ||
      market.GameType === 1853
    ) {
      if (market.GameType === 1853) {
        market.teamHome = `${this.getShortName(market.teamHome)} `;
        market.teamAway = `${this.getShortName(market.teamAway)} `;
        newArraySpovs.push(`Más ${market.spovListOfKeys.Spov}`);
        newArraySpovs.push(`Menos ${market.spovListOfKeys.Spov}`);
      } else {
        market.teamHome = `${this.getShortName(market.teamHome)} ${this.getSymbol(
          parseFloat(market.spovListOfKeys.realHcap),
        )} ${parseFloat(market.spovListOfKeys.Hcap)}`;
        market.teamAway = `${this.getShortName(market.teamAway)} ${this.getSymbol(
          -1 * parseFloat(market.spovListOfKeys.realHcap),
        )} ${parseFloat(market.spovListOfKeys.Hcap)}`;
        newArraySpovs.push(`Más ${market.spovListOfKeys.Totals}`);
        newArraySpovs.push(`Menos ${market.spovListOfKeys.Totals}`);
      }
    } else if (market.GameType === 1647) {
      newArraySpovs.push(
        `${this.getShortName(market.teamHome)} ${this.getSymbol(
          parseFloat(market.spovListOfKeys.realHcap),
        )} ${parseFloat(market.spovListOfKeys.Hcap)}`,
      );
      newArraySpovs.push(
        `${this.getShortName(market.teamAway)} ${this.getSymbol(
          -1 * parseFloat(market.spovListOfKeys.realHcap),
        )} ${parseFloat(market.spovListOfKeys.Hcap)}`,
      );
    } else {
      newArraySpovs.push(`Más ${parseFloat(market.spovListOfKeys.Hcap)}`);
      newArraySpovs.push(`Menos ${parseFloat(market.spovListOfKeys.Hcap)}`);
    }

    market.AllSpov = newArraySpovs;
  }

  private getGridWithFourPositions(market: any, matrix: any) {
    market.combinedResults = [];
    market.AllSpov.forEach((spov: any, indexSpov: any) => {
      const row: any = [];
      matrix.forEach((matrixOrder: any, indexMatix: any) => {
        let current = market.GroupResults.find((m: any) => m.SortOrder === matrixOrder);
        if (!current) {
          current = this.declareNewResultMissing();
        }
        if (row.length < 2 && indexSpov === 0 && indexMatix < 2) {
          row.push(current);
        }
        if (row.length < 2 && indexSpov === 1 && indexMatix < 4 && indexMatix >= 2) {
          row.push(current);
        }
      });
      if (row.length > 0) market.combinedResults.push({ results: row, spov: spov });
    });
  }

  private getTotalSpovsList(market: any) {
    const newArraySpovs: any = [];
    market.AllSpov = [];
    let actualSpov: any;
    market.GroupResults.forEach((result: any) => {
      if (actualSpov !== result.Spov) {
        market.AllSpov.push(result.Spov);
        actualSpov = result.Spov;
      }
    });

    market.AllSpov.forEach((spov: any) => {
      const otherSpoves = spov.split("|");
      otherSpoves.forEach((key: any) => {
        if (market.GameType === 1330) {
          newArraySpovs.push(`Más ${key.match(this.regexValues)[0]}`);
          newArraySpovs.push(`Menos ${key.match(this.regexValues)[0]}`);
        } else {
          newArraySpovs.push(key.match(this.regexValues)[0]);
        }
      });
    });
    market.AllSpov = newArraySpovs;
  }

  private getGridWithSixPositions(market: any, matrix: number[]) {
    market.combinedResults = [];

    market.AllSpov.forEach((spov: any) => {
      const filteredResults = market.GroupResults.filter(
        (result: any) =>
          result.Spov.substr(result.Spov.indexOf(">") + 1) === Math.abs(parseFloat(spov)).toString(),
      );

      const rowInFirstPositionGrid: any = [];
      const rowInSecondPositionGrid: any = [];
      matrix.forEach((matrixOrder) => {
        let current = filteredResults.find((m: any) => m.SortOrder === matrixOrder);
        if (!current) {
          current = this.declareNewResultMissing();
        }
        //current.title = spov;
        if (matrixOrder % 2 === 0) {
          rowInFirstPositionGrid.push(current);
        } else {
          rowInSecondPositionGrid.push(current);
        }
      });
      const arrOfRows = [rowInFirstPositionGrid, rowInSecondPositionGrid];
      market.combinedResults.push({
        results: arrOfRows,
        spov: this.getFinalSpovsByMarketName(market.Name, spov, market.GameType),
      });
    });
  }

  private getAllSpovsWithOutSpovs(market: any) {
    if (market.GameType === 988 || market.GameType === 990 || market.GameType === 1064) {
      return ["Si", "No"];
    }
    //Todo : this crash
    try {
      return [
        market.GroupResults[2].Name.split(" y ")[0],
        market.GroupResults[2].Name.split(" y ")[1],
        "Sin Goles",
      ];
    } catch {
      return ["1", "2", "Sin Goles"];
    }
  }

  private getGridWithSevenPositions(market: any, matrix: any) {
    market.combinedResults = [];
    market.AllSpov.forEach((spov: any, indexSpov: any) => {
      const row: any = [];
      this.getRowsOfGridInSevenPositions(matrix, market, row, indexSpov);
      if (row.length > 0) market.combinedResults.push({ results: row, spov: spov });
    });
  }

  private getRowsOfGridInSevenPositions(matrix: any, market: any, row: any[], indexSpov: any) {
    matrix.forEach((matrixOrder: any, indexMatix: any) => {
      let current = market.GroupResults.find((m: any) => m.SortOrder === matrixOrder);
      if (!current) {
        current = this.declareNewResultMissing();
      }
      if (row.length < 3 && indexSpov === 0 && indexMatix < 3) {
        row.push(current);
      }
      if (row.length < 3 && indexSpov === 1 && indexMatix < 6 && indexMatix >= 3) {
        row.push(current);
      }
      if (row.length < 3 && indexSpov === 2 && indexMatix >= 6 && matrix.length > 7) {
        row.push(current);
      }
    });
  }

  public declareNewResultMissing(): C_Result {
    return new C_Result("-", null, null, null, null, null, null, null, null, null, null, null);
  }

  isTennisEvent(liveMarket) {
    return liveMarket.SportHandle === SportHandleList.tennis;
  }

  // Fast bets methods
  private getFastBets(liveMarket: C_EventLive, actualMarket) {
    let fastBet;
    if (this.isTennisEvent(liveMarket)) {
      const ev = this.fastBetService.parseLiveDataGames(liveMarket);
      if (!ev) {
        return;
      }
      const fastGames = [];
      this.fastBetService.fastBetMarkets(ev, actualMarket).forEach((bet) => {
        if (bet.Games.length < 1) {
          return;
        }
        bet.headerClass = "sb-grid-header--single";
        const titles = this.fastBetService.getGridTitleHeader(bet);
        const fastBetsGames = [];
        let orderZero: C_Result[];
        let orderOne: C_Result[];
        let isSoftCombined = false;
        if (bet.Games.length > 1) {
          [orderZero, orderOne] = this.getResultsFastBet(bet.Games);
          fastBetsGames.push(orderZero);
          fastBetsGames.push(orderOne);
        } else {
          isSoftCombined = true;
          [orderZero, orderOne] = this.getResultsFastBet(bet.Games);
          fastBetsGames.push(orderZero);
          fastBetsGames.push(orderOne);
        }
        const namesOfTeams = this.getTeamsName(bet);
        fastBet = this.getFastBetTennis(bet, fastBetsGames, namesOfTeams, titles, liveMarket);
        if (this.checkFastBetMarketIsValid(fastBet)) {
          this.lockInvalidFastBetGameTypeIds(fastBet);
          fastGames.push(fastBet);
        }
      });
      return fastGames;
    }
  }

  private lockInvalidFastBetGameTypeIds(fastBet) {
    fastBet.GroupResults.forEach((group) => {
      if (group.GameTypeId !== 1636 && group.GameTypeId !== 1972) {
        group.Locked = true;
      }
    });

    fastBet.combinedResults.forEach((group) => {
      if (group.GameTypeId !== 1636 && group.GameTypeId !== 1972) {
        group.Locked = true;
      }
    });
  }

  private getTeamsName(bet: any): string[] {
    const teamNames = [bet.Name.split(" - ")[0], bet.Name.split(" - ")[1]];
    return teamNames;
  }

  private getFastBetTennisName(bet: any): string {
    let name = `Mercados Rapidos`;

    // Name "Ganador del Punto 7 en el Juego 7 del Set 1"
    // "Ganador del juego (Juego 7 / Set 1)"

    if (bet.Games && bet.Games[0] && (bet.Games[0].GameType === 1636 || bet.Games[0].GameType === 1972)) {
      const setIndex = bet.Games[0].Name.indexOf("Set");
      const setString = bet.Games[0].Name.substring(setIndex, setIndex + 5);

      name += ` ${setString}`;
    }

    return name;
  }

  private getFastBetTennis(bet: any, fastBetsGames: any[], namesOfTeams: string[], titles: any, ev: any) {
    const name = this.getFastBetTennisName(bet);

    return C_GameGroupByName.parse({
      AllSpov: titles,
      Name: name,
      nameLeague: bet.Games[0].nameLeague,
      Priority: bet.Games[0].Priority,
      CategoryInfos: [
        {
          CategoryId: "0",
          CategoryName: "MERCADOS RAPIDOS",
          IsRelevant: false,
        },
      ],
      CategoryInfo: {
        CategoryId: "0",
        CategoryName: "MERCADOS RAPIDOS",
        IsRelevant: false,
      },
      DisplayTypeName: "2buttonline",
      teamAway: namesOfTeams[1],
      teamHome: titles.tileCenter,
      Locked: this.getEventIsLocked(ev, bet),
      titleTeams: "Ganador",
      titleCenter: namesOfTeams[0],
      isSoftCombined: false,
      isHardCombined: false,
      isSpecialMarket: false,
      isCombined: false,
      isFastGamesHidden: false,
      headerClass: "sb-grid-header--single",
      displayMode: "expanded",
      GroupResults: fastBetsGames[0],
      combinedResults: fastBetsGames[1],
    });
  }

  private getEventIsLocked(ev: any, bet: any): boolean {
    let isEventLocked: boolean = false;
    if (ev.Locked) {
      return ev.Locked;
    }
    bet.Games.forEach((game) => {
      if (game.Locked) {
        isEventLocked = game.Locked;
      }
    });

    return isEventLocked;
  }

  private checkFastBetMarketIsValid(fastBet): boolean {
    let isValid = false;
    fastBet.GroupResults.forEach((r) => {
      if (r.SortOrder) {
        isValid = true;
      }
    });

    fastBet.combinedResults.forEach((r) => {
      if (r.SortOrder) {
        isValid = true;
      }
    });
    return isValid;
  }

  getResultsFastBet(Games: any): [C_Result[], C_Result[]] {
    const orderZero: C_Result[] = [];
    const orderOne: C_Result[] = [];
    Games.forEach((game) => {
      if (!game?.GroupResults || !game?.GroupResults.length) {
        orderZero.push(this.declareNewResultMissing());
        orderOne.push(this.declareNewResultMissing());
        return;
      }
      game?.GroupResults.forEach((res) => {
        if (res.SortOrder === 0) {
          orderZero.push(res);
          return;
        }
        orderOne.push(res);
      });
    });
    return [orderZero, orderOne];
  }

  private getFinalSpovsByMarketName(name: any, spov: any, gameType: number): string[] {
    const expressionPrefix = /([A-Za-z/á]+[/]+[A-Za-z]+)/g;
    let spovPrefixList = name.match(expressionPrefix) ? name.match(expressionPrefix)[0].split("/") : "";
    if (gameType === 39 && name === "1X2 y Total de Goles") {
      spovPrefixList = ["Más", "Menos"];
    }
    return [
      `${spovPrefixList[0]} ${spov.substr(spov.indexOf(">") + 1)}`,
      `${spovPrefixList[1]}  ${spov.substr(spov.indexOf(">") + 1)}`,
    ];
  }

  public getAllSpovesInMarket(groupedMarkets: any[], market: any) {
    if (!groupedMarkets[groupedMarkets.length - 1]) return;
    groupedMarkets[groupedMarkets.length - 1].AllSpov.push(market.Spov);
  }

  getSymbol(hCap: number) {
    return Math.sign(hCap) > 0 ? "+" : "-";
  }

  public isBetbuilderSoccerEvent(market) {
    const betbuilderGames = [];
    market.Games?.length &&
      market.Games.forEach((i) => {
        if (i.SmartMarketAvailable) betbuilderGames.push(i);
      });
    return market.SportHandle === SportHandleList.soccer && betbuilderGames.length > 0;
  }

  public setLiveMarketCategories(data: any, market: any) {
    let cats = this.arrangeCategories(data.CategoryInfos, []);
    if (
      this.isBetbuilderSoccerEvent(data.Event) &&
      cats.length > 0 &&
      this.globalVars.FEATURES.SPO_LiveBetbuilderEnabled
    ) {
      cats = this.createBetbuilderFilterSoccer(cats);
    }
    return cats;
  }

  public createBetbuilderFilterSoccer(categories: C_CategoryInfo[]) {
    let newCategoriesArr: C_CategoryInfo[] = Array.from(categories);
    const principalesMarketsCat = categories.find((i) => i.CategoryId === SportsConstants.principalesCat);
    const betbuilderBetsCat = categories.find((i) => i.CategoryId === SportsConstants.betbuilderCatId);
    // const betbuilderBetsCat = new C_CategoryInfo(SportsConstants.betbuilderCatId, false, SportsConstants.betbuilderCatName);
    newCategoriesArr = newCategoriesArr.filter(
      (i) => i.CategoryId !== SportsConstants.principalesCat && i.CategoryId !== SportsConstants.betbuilderCatId,
    );
    if (betbuilderBetsCat) newCategoriesArr.unshift(betbuilderBetsCat);
    if (principalesMarketsCat) newCategoriesArr.unshift(principalesMarketsCat);
    return newCategoriesArr;
  }
  arrangeCategories(categories: any[], previousCategories: C_CategoryInfo[]): C_CategoryInfo[] {
    previousCategories = categories.map((i) => {
      const newCat: C_CategoryInfo = new C_CategoryInfo(i.CategoryId, i.IsRelevant, i.CategoryName);
      newCat.numGames = 1;
      previousCategories.push(newCat);
      return newCat;
    });
    return previousCategories;
  }
}
